import { useLocation, useNavigate } from "react-router-dom";
import { Bot, ChevronDown, Folder, Home, Notebook, Shield } from "lucide-react";
import { ManifoldMonoBadgeIcon } from "~/assets/icons";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import useUserPermission from "~/hooks/useUserPermission";
import { cn } from "~/lib/utils";
import { useCanUseAIAgents } from "~/pages/manifold-nextgen/hooks/useCanUseAIAgents";
import { useCanUseProjects } from "~/pages/manifold-nextgen/hooks/useCanUseProjects";

interface ManifoldNextGenNavPickerProps {
  // Renders the pill for display over the colored home hero: translucent
  // white surface + white text/icons, matching the design's "Home" nav
  // button treatment, instead of the normal opaque light/dark surface.
  colored?: boolean;
}

export function ManifoldNextGenNavPicker({ colored = false }: ManifoldNextGenNavPickerProps = {}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const canViewAdmin = useUserPermission("view:admin-app");
  const canUseProjects = useCanUseProjects();
  const canUseAgents = useCanUseAIAgents();

  const NAV_SECTIONS = [
    {
      label: "Home",
      description: "Search, recent sessions, and quick access",
      href: "/nextgen",
      Icon: Home,
      exact: true,
    },
    {
      label: "Projects",
      description: "Organize research projects",
      href: "/nextgen/projects",
      Icon: Notebook,
      hidden: !canUseProjects,
    },
    {
      label: "Datasets",
      description: "Explore datasets and files",
      href: "/nextgen/datasets",
      Icon: Folder,
    },
    {
      label: "Agents",
      description: "Manage AI-powered research assistants",
      href: "/nextgen/agents",
      Icon: Bot,
      hidden: !canUseAgents,
    },
    {
      label: "Admin",
      description: "Manage users, groups, and platform tools",
      href: "/nextgen/admin",
      // /nextgen/admin has siblings outside the Admin sidebar (conversation
      // review) — match only the sidebar's own routes so the pill doesn't light
      // up "Admin" while viewing those other pages.
      matchPrefixes: [
        "/nextgen/admin/users",
        "/nextgen/admin/groups",
        "/nextgen/admin/turn-diagnostics",
      ],
      Icon: Shield,
      hidden: !canViewAdmin,
    },
  ];

  const navSections = NAV_SECTIONS.filter((s) => !s.hidden);

  const activeSection = navSections.find((s) => {
    if ("exact" in s && s.exact) {
      return pathname === s.href;
    }
    const prefixes = ("matchPrefixes" in s ? s.matchPrefixes : undefined) ?? [s.href];
    return prefixes.some((prefix) => pathname.startsWith(prefix));
  });
  const sectionLabel = activeSection?.label ?? "";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          data-testid="nextgen-nav-picker-trigger"
          className={cn(
            "flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium text-foreground duration-300 focus-visible:ring-0 dark:bg-[var(--dt-primitives-color-neutral-800)] dark:shadow-none dark:hover:bg-[var(--dt-primitives-color-neutral-900)]",
            colored
              ? "border-white/20 bg-white/10 text-white hover:bg-white/20 hover:text-white focus-visible:border-white/20 dark:bg-white/10 dark:hover:bg-white/20 dark:hover:text-white"
              : "border-transparent focus-visible:border-transparent"
          )}
          // Always set (never added/removed) so box-shadow/none interpolate
          // smoothly across the colored/default transition instead of
          // popping in — an absent `style` attribute can't be transitioned.
          style={{ boxShadow: colored ? "none" : "var(--mds-shadow-sm)" }}
        >
          <ManifoldMonoBadgeIcon
            className={cn(
              "size-5 shrink-0 transition-colors duration-300",
              colored && "text-white"
            )}
          />
          {sectionLabel && <span>{sectionLabel}</span>}
          <ChevronDown
            className={cn(
              "size-3.5 shrink-0 text-muted-foreground transition-colors duration-300",
              colored && "text-white/70"
            )}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="flex flex-col p-1.5">
        {navSections.map(({ label, description, href, Icon }) => (
          <DropdownMenuItem
            key={href}
            onClick={() => navigate(href)}
            className="group flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 transition-colors duration-150 focus:bg-primary/10 focus:text-foreground dark:focus:bg-primary/20"
          >
            <Icon className="size-5 shrink-0 text-muted-foreground transition-colors duration-150 group-focus:text-primary" />
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium leading-snug text-foreground">{label}</span>
              <span className="text-xs leading-snug text-muted-foreground">{description}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
